import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { DespachoInfoCallout, DespachoSectionCard } from "@/components/despacho/DespachoUi";
import { despachoFiscalInfoApi, parseFiscalInfoError } from "@/modules/despacho/despachoFiscalInfoApi";

const WEEKDAY_LABELS = {
  mon: "Lunes",
  tue: "Martes",
  wed: "Miércoles",
  thu: "Jueves",
  fri: "Viernes",
  sat: "Sábado",
  sun: "Domingo",
};

function formatScheduleBlock(schedule) {
  const weekdays = schedule?.weekdays || {};
  return Object.entries(WEEKDAY_LABELS).map(([key, label]) => {
    const slot = weekdays[key];
    if (!slot) return `${label}: no laborable`;
    return `${label}: ${slot.start || "—"} – ${slot.end || "—"}`;
  });
}

export default function CalendarioConfigPanel({ clientScheduleOnly = false }) {
  const [config, setConfig] = useState(null);
  const [loadState, setLoadState] = useState("loading");

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const data = await despachoFiscalInfoApi.getCalendarConfig();
      setConfig(data.config || data);
      setLoadState("ready");
    } catch (err) {
      setConfig(null);
      setLoadState(parseFiscalInfoError(err).kind === "forbidden" ? "forbidden" : "error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loadState === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Cargando configuración de calendarios…
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="space-y-4" data-testid="calendario-config-panel">
      {!clientScheduleOnly ? (
        <DespachoInfoCallout testId="calendario-config-notice">
          Los vencimientos <strong>fiscales</strong> se recorren al siguiente día hábil oficial (sábado, domingo y
          festivos federales). Las fechas <strong>operativas</strong> y de <strong>mensajes a clientes</strong> usan
          días hábiles del despacho y el horario laboral configurado.
        </DespachoInfoCallout>
      ) : (
        <DespachoInfoCallout testId="calendario-config-notice">
          Horario permitido para <strong>mensajes y recordatorios a clientes</strong> (L–V 9:00–18:00, Sáb 9:00–13:00
          por defecto). Fuera de este horario el sistema reprograma al siguiente slot laboral.
        </DespachoInfoCallout>
      )}

      <div className={`grid gap-4 ${clientScheduleOnly ? "" : "md:grid-cols-2"}`}>
        {!clientScheduleOnly ? (
          <DespachoSectionCard title="Horario operativo del despacho">
            <ul className="text-sm text-slate-700 space-y-1 m-0 pl-4 list-disc">
              {formatScheduleBlock(config.work_schedule).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </DespachoSectionCard>
        ) : null}

        <DespachoSectionCard title="Horario mensajes a clientes">
          <ul className="text-sm text-slate-700 space-y-1 m-0 pl-4 list-disc">
            {formatScheduleBlock(config.client_message_schedule).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </DespachoSectionCard>
      </div>

      {!clientScheduleOnly ? (
        <DespachoSectionCard title="Ajuste fiscal">
          <p className="text-sm text-slate-600 m-0">
            {config.adjust_fiscal !== false
              ? "Activo: día inhábil fiscal → siguiente día hábil."
              : "Desactivado: se usa la fecha base sin recorrer."}
          </p>
        </DespachoSectionCard>
      ) : null}
    </div>
  );
}
